import { useMemo } from "react";

import { BackgroundVideo } from "@ui/components/BackgroundVideo";
import { ResponsiveImage } from "@ui/components/ResponsiveImage";
import { useDeviceBreakpoint } from "@ui/hooks/useDeviceBreakpoint";
import classNames from "classnames";

import type { HeroBackgroundProps } from "../types/HeroV2Types";

import "../HeroV2.global.scss";
import styles from "./HeroBackground.module.scss";

export const HeroBackground = ({
  backgroundType,
  desktopImage,
  mediumImage,
  smallImage,
  tabletVideoFileReference,
  tabletVideoFileType,
  mobileVideoFileReference,
  mobileVideoFileType,
  videoFileReference,
  videoFileType,
  isHeroCardsShown,
  removeVideoRoundedCorners,
  imageClassName,
  videoClassName,
  renderBackgroundImage,
  setIsVideoLoaded,
  isFirstElement,
}: HeroBackgroundProps) => {
  const { breakpoint } = useDeviceBreakpoint();
  const classes = classNames(styles.heroBackground, "heroBackgroundWrapper", {
    [styles.isHeroCardsShown]: isHeroCardsShown,
  });

  const { altText, title } = useMemo(() => {
    if (breakpoint === "mobile") {
      return { altText: smallImage?.alt ?? "", title: smallImage?.title };
    }
    if (breakpoint === "tablet") {
      return { altText: mediumImage?.alt ?? "", title: mediumImage?.title };
    }

    return { altText: desktopImage?.alt ?? "", title: desktopImage?.title };
  }, [breakpoint, desktopImage, mediumImage, smallImage]);

  return (
    <div className={classes} data-testid="hero-background">
      {backgroundType === "image" &&
        (renderBackgroundImage ? (
          renderBackgroundImage({ desktopImage, imageClassName, mediumImage, smallImage })
        ) : (
          <ResponsiveImage
            largeSrc={desktopImage?.src || ""}
            mediumSrc={mediumImage?.src}
            alt={altText}
            title={title}
            smallSrc={smallImage?.src}
            className={imageClassName}
          />
        ))}
      {backgroundType === "video" && (
        <BackgroundVideo
          className={classNames("videoContainer", videoClassName)}
          desktopSrc={videoFileReference || ""}
          desktopType={videoFileType}
          tabletSrc={tabletVideoFileReference}
          tabletType={tabletVideoFileType}
          mobileSrc={mobileVideoFileReference}
          mobileType={mobileVideoFileType}
          removeRoundedCorners={removeVideoRoundedCorners}
          onReady={isFirstElement ? () => setIsVideoLoaded?.(true) : undefined}
        />
      )}
    </div>
  );
};
